import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { 
  Star, 
  ArrowRight, 
  CheckCircle, 
  Scissors,
  Building2,
  RefreshCw,
  Home,
  Award
} from 'lucide-react';

const PublicRating = () => {
  const navigate = useNavigate();
  const { language, toggleLanguage } = useLanguage();
  
  // Math CAPTCHA state
  const [mathQuestion, setMathQuestion] = useState({ num1: 0, num2: 0, operator: '+' });
  const [mathAnswer, setMathAnswer] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [verificationStep, setVerificationStep] = useState(1);
  const [mathQuestion2, setMathQuestion2] = useState({ num1: 0, num2: 0, operator: '+' });
  const [mathAnswer2, setMathAnswer2] = useState('');
  
  // Rating state
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [salonRating, setSalonRating] = useState(0);
  const [salonComment, setSalonComment] = useState('');
  const [barberRatings, setBarberRatings] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    generateMathQuestion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isVerified) {
      fetchBarbers();
    }
  }, [isVerified]);

  const generateMathQuestion = () => {
    const operators = ['+', '-', '×'];
    const operator = operators[Math.floor(Math.random() * operators.length)];
    let num1, num2;
    
    if (operator === '×') {
      num1 = Math.floor(Math.random() * 10) + 1;
      num2 = Math.floor(Math.random() * 10) + 1;
    } else if (operator === '-') {
      num1 = Math.floor(Math.random() * 50) + 20;
      num2 = Math.floor(Math.random() * num1);
    } else {
      num1 = Math.floor(Math.random() * 50) + 1;
      num2 = Math.floor(Math.random() * 50) + 1;
    }
    
    if (verificationStep === 1) {
      setMathQuestion({ num1, num2, operator });
      setMathAnswer('');
    } else {
      setMathQuestion2({ num1, num2, operator });
      setMathAnswer2('');
    }
  };

  const calculateAnswer = (question) => {
    const { num1, num2, operator } = question;
    switch (operator) {
      case '+': return num1 + num2;
      case '-': return num1 - num2;
      case '×': return num1 * num2;
      default: return 0;
    }
  };

  const handleVerifyStep1 = () => {
    const correctAnswer = calculateAnswer(mathQuestion);
    if (parseInt(mathAnswer) === correctAnswer) {
      // Generate second question first before changing step
      const operators = ['+', '-', '×'];
      const operator = operators[Math.floor(Math.random() * operators.length)];
      let num1, num2;
      
      if (operator === '×') {
        num1 = Math.floor(Math.random() * 10) + 1;
        num2 = Math.floor(Math.random() * 10) + 1;
      } else if (operator === '-') {
        num1 = Math.floor(Math.random() * 50) + 20;
        num2 = Math.floor(Math.random() * num1);
      } else {
        num1 = Math.floor(Math.random() * 50) + 1;
        num2 = Math.floor(Math.random() * 50) + 1;
      }
      
      setMathQuestion2({ num1, num2, operator });
      setMathAnswer2('');
      setVerificationStep(2);
      toast.success(language === 'ar' ? 'صحيح! السؤال الثاني' : 'Correct! Second question');
    } else {
      toast.error(language === 'ar' ? 'إجابة خاطئة، حاول مرة أخرى' : 'Wrong answer, try again');
      generateMathQuestion();
    }
  };

  const handleVerifyStep2 = () => {
    const correctAnswer = calculateAnswer(mathQuestion2);
    if (parseInt(mathAnswer2) === correctAnswer) {
      setIsVerified(true);
      toast.success(language === 'ar' ? 'تم التحقق بنجاح!' : 'Verification successful!');
    } else {
      toast.error(language === 'ar' ? 'إجابة خاطئة، حاول مرة أخرى' : 'Wrong answer, try again');
      setVerificationStep(1);
      generateMathQuestion();
      setMathAnswer('');
    }
  };

  const fetchBarbers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/public/barbers');
      setBarbers(response.data || []);
    } catch (error) {
      console.error('Error fetching barbers:', error);
      toast.error(language === 'ar' ? 'فشل تحميل قائمة الحلاقين' : 'Failed to load barbers');
    } finally {
      setLoading(false);
    }
  };

  const handleBarberRating = (barberId, rating) => {
    setBarberRatings(prev => ({
      ...prev,
      [barberId]: { ...prev[barberId], rating }
    }));
  };

  const handleBarberComment = (barberId, comment) => {
    setBarberRatings(prev => ({
      ...prev,
      [barberId]: { ...prev[barberId], comment }
    }));
  };

  const handleSubmit = async () => {
    // Validate at least salon rating is provided
    if (salonRating === 0) {
      toast.error(language === 'ar' ? 'يرجى تقييم الصالون' : 'Please rate the salon');
      return;
    }

    setSubmitting(true);
    try {
      // Submit salon rating
      await api.post('/ratings/public/salon', {
        rating: salonRating,
        comment: salonComment || ''
      });

      // Submit barber ratings
      for (const [barberId, data] of Object.entries(barberRatings)) {
        if (data.rating && data.rating > 0) {
          await api.post('/ratings/public/barber', {
            barber_id: barberId,
            rating: data.rating,
            comment: data.comment || ''
          });
        }
      }

      setSubmitted(true);
      toast.success(language === 'ar' ? 'شكراً لتقييمك!' : 'Thank you for your rating!');
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل إرسال التقييم' : 'Failed to submit rating'));
    } finally {
      setSubmitting(false);
    }
  };

  // Verification Screen
  if (!isVerified) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1622622291585-8d45fc593a55?q=80&w=1920&auto=format&fit=crop')] bg-cover bg-center opacity-5"></div>
        
        {/* Navigation */}
        <div className="absolute top-6 start-6 flex items-center gap-3 z-10">
          <Button
            data-testid="home-btn"
            onClick={() => navigate('/')}
            className="bg-primary/20 backdrop-blur-sm border border-primary/30 hover:bg-primary/30"
            size="sm"
          >
            <Home className="w-4 h-4 me-2" />
            {language === 'ar' ? 'الرئيسية' : 'Home'}
          </Button>
        </div>

        <Button
          onClick={toggleLanguage}
          variant="outline"
          size="sm"
          className="absolute top-6 end-6 z-10 bg-card/50 backdrop-blur-sm border-white/10"
        >
          {language === 'ar' ? 'EN 🇺🇸' : '🇸🇦 عربي'}
        </Button>

        <div className="relative z-10 w-full max-w-md">
          <div className="bg-card/95 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
            {/* Logo */}
            <div className="text-center mb-8">
              <img 
                src="/logo-gold.png" 
                alt="KE Gents Saloon" 
                className="h-24 mx-auto mb-4"
              />
              <h1 className="text-2xl font-bold text-white">
                {language === 'ar' ? 'تقييم الصالون' : 'Rate Us'}
              </h1>
              <p className="text-muted-foreground mt-2">
                {language === 'ar' 
                  ? 'أجب على السؤالين للتحقق' 
                  : 'Answer two questions to verify'}
              </p>
            </div>

            {/* Progress */}
            <div className="flex justify-center gap-2 mb-6">
              <div className={`w-3 h-3 rounded-full ${verificationStep >= 1 ? 'bg-primary' : 'bg-white/20'}`} />
              <div className={`w-3 h-3 rounded-full ${verificationStep >= 2 ? 'bg-primary' : 'bg-white/20'}`} />
            </div>

            {/* Question */}
            <div className="space-y-4">
              <div className="bg-primary/10 border border-primary/20 rounded-xl p-6 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  {language === 'ar' ? `السؤال ${verificationStep} من 2` : `Question ${verificationStep} of 2`}
                </p>
                <p className="text-3xl font-bold text-white mb-4" dir="ltr">
                  {verificationStep === 1 
                    ? `${mathQuestion.num1} ${mathQuestion.operator} ${mathQuestion.num2} = ?`
                    : `${mathQuestion2.num1} ${mathQuestion2.operator} ${mathQuestion2.num2} = ?`
                  }
                </p>
                <input
                  type="number"
                  value={verificationStep === 1 ? mathAnswer : mathAnswer2}
                  onChange={(e) => verificationStep === 1 ? setMathAnswer(e.target.value) : setMathAnswer2(e.target.value)}
                  className="w-full text-center text-2xl p-4 rounded-xl bg-background border border-white/10 text-white focus:border-primary focus:outline-none"
                  placeholder="?"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      verificationStep === 1 ? handleVerifyStep1() : handleVerifyStep2();
                    }
                  }}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    if (verificationStep === 1) {
                      setMathQuestion({ ...mathQuestion });
                      generateMathQuestion();
                    } else {
                      generateMathQuestion();
                    }
                  }}
                  variant="outline"
                  className="flex-1 border-white/10"
                >
                  <RefreshCw className="w-4 h-4 me-2" />
                  {language === 'ar' ? 'سؤال آخر' : 'New Question'}
                </Button>
                <Button
                  onClick={verificationStep === 1 ? handleVerifyStep1 : handleVerifyStep2}
                  className="flex-1 bg-primary text-primary-foreground"
                  disabled={verificationStep === 1 ? !mathAnswer : !mathAnswer2}
                >
                  <CheckCircle className="w-4 h-4 me-2" />
                  {language === 'ar' ? 'تحقق' : 'Verify'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success Screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1622622291585-8d45fc593a55?q=80&w=1920&auto=format&fit=crop')] bg-cover bg-center opacity-5"></div>
        
        <div className="relative z-10 w-full max-w-md text-center">
          <div className="bg-card/95 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {language === 'ar' ? 'شكراً لك!' : 'Thank You!'}
            </h1>
            <p className="text-muted-foreground mb-6">
              {language === 'ar' 
                ? 'تقييمك يساعدنا على تحسين خدماتنا'
                : 'Your rating helps us improve our services'}
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-primary text-primary-foreground"
            >
              <Home className="w-4 h-4 me-2" />
              {language === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Rating Form
  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1622622291585-8d45fc593a55?q=80&w=1920&auto=format&fit=crop')] bg-cover bg-center opacity-5 fixed"></div>
      
      {/* Navigation */}
      <div className="fixed top-6 start-6 flex items-center gap-3 z-20">
        <Button
          data-testid="home-btn"
          onClick={() => navigate('/')}
          className="bg-primary/20 backdrop-blur-sm border border-primary/30 hover:bg-primary/30"
          size="sm"
        >
          <Home className="w-4 h-4 me-2" />
          {language === 'ar' ? 'الرئيسية' : 'Home'}
        </Button>
      </div>

      <Button
        onClick={toggleLanguage}
        variant="outline"
        size="sm"
        className="fixed top-6 end-6 z-20 bg-card/50 backdrop-blur-sm border-white/10"
      >
        {language === 'ar' ? 'EN 🇺🇸' : '🇸🇦 عربي'}
      </Button>

      <div className="relative z-10 max-w-2xl mx-auto pt-16">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="/logo-gold.png" 
            alt="KE Gents Saloon" 
            className="h-20 mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-white mb-2">
            {language === 'ar' ? 'قيّم تجربتك' : 'Rate Your Experience'}
          </h1>
          <p className="text-muted-foreground">
            {language === 'ar' 
              ? 'رأيك يهمنا لتحسين خدماتنا'
              : 'Your feedback helps us improve'}
          </p>
        </div>

        {/* Salon Rating */}
        <div className="bg-card/95 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mb-6" data-testid="salon-rating-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {language === 'ar' ? 'تقييم الصالون' : 'Salon Rating'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {language === 'ar' ? 'كيف كانت تجربتك الإجمالية؟' : 'How was your overall experience?'}
              </p>
            </div>
          </div>

          {/* Stars */}
          <div className="flex justify-center gap-2 mb-4">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setSalonRating(star)}
                className="p-1 transition-transform hover:scale-110"
                data-testid={`salon-star-${star}`}
              >
                <Star
                  className={`w-10 h-10 ${
                    star <= salonRating
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-600 hover:text-yellow-400/50'
                  }`}
                />
              </button>
            ))}
          </div>

          <Textarea
            value={salonComment}
            onChange={(e) => setSalonComment(e.target.value)}
            placeholder={language === 'ar' ? 'اكتب تعليقك هنا (اختياري)...' : 'Write your comment here (optional)...'}
            className="bg-background border-white/10 resize-none"
            rows={3}
          />
        </div>

        {/* Barbers Rating */}
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : barbers.length > 0 && (
          <div className="bg-card/95 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mb-6" data-testid="barbers-rating-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <Scissors className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {language === 'ar' ? 'تقييم الحلاقين' : 'Rate Barbers'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {language === 'ar' ? 'قيّم الحلاق الذي خدمك (اختياري)' : 'Rate the barber who served you (optional)'}
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {barbers.map((barber) => (
                <div 
                  key={barber.id} 
                  className="bg-secondary/30 rounded-xl p-4"
                  data-testid={`barber-rating-${barber.id}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                      <Award className="w-5 h-5 text-primary" />
                    </div>
                    <span className="font-semibold text-white">{barber.name}</span>
                  </div>

                  {/* Stars */}
                  <div className="flex gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => handleBarberRating(barber.id, star)}
                        className="p-0.5 transition-transform hover:scale-110"
                      >
                        <Star
                          className={`w-7 h-7 ${
                            star <= (barberRatings[barber.id]?.rating || 0)
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-600 hover:text-yellow-400/50'
                          }`}
                        />
                      </button>
                    ))}
                  </div>

                  <Textarea
                    value={barberRatings[barber.id]?.comment || ''}
                    onChange={(e) => handleBarberComment(barber.id, e.target.value)}
                    placeholder={language === 'ar' ? 'تعليق اختياري...' : 'Optional comment...'}
                    className="bg-background border-white/10 resize-none text-sm"
                    rows={2}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={submitting || salonRating === 0}
          className="w-full bg-primary text-primary-foreground py-6 text-lg"
          data-testid="submit-rating-btn"
        >
          {submitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white me-2"></div>
              {language === 'ar' ? 'جاري الإرسال...' : 'Submitting...'}
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5 me-2" />
              {language === 'ar' ? 'إرسال التقييم' : 'Submit Rating'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default PublicRating;
